import * as React from 'react';
import clsx from 'clsx';
import { useControllable, useLayoutEffect } from '../../hooks';
import { makeId } from '../../utils';
import { Base } from '../Base';
import { SrOnly } from '../Helper';
import { SegmentedControlProvider, useSegmentedControlContext } from './context';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
var styles = {
  "root": "SegmentedControl-root_u3rAn",
  "label": "SegmentedControl-label_ubh7y",
  "content": "SegmentedControl-content_1gV03",
  "indicator": "SegmentedControl-indicator_3OnCg",
  "size-s": "SegmentedControl-size-s_2eEwj",
  "size-m": "SegmentedControl-size-m_1uhJk",
  "size-l": "SegmentedControl-size-l_2Nm8B",
  "input": "SegmentedControl-input_3pQEQ"
};
const defaultIndicatorStyle = {};
function SegmentedControl({
  name,
  defaultValue,
  value: valueProp,
  size = 'm',
  legend,
  children,
  className,
  onValueChange,
  ...extraProps
}) {
  const rootRef = React.useRef(null);
  const indicatorRef = React.useRef(null);
  const observerRef = React.useRef();
  const shouldAnimate = React.useRef(false);
  const [indicatorStyle, setIndicatorStyle] = React.useState(defaultIndicatorStyle);
  const [value, setValue] = useControllable({
    prop: valueProp,
    defaultProp: defaultValue,
    onChange: onValueChange
  });
  function updateIndicator() {
    if (!rootRef.current) return;
    const items = rootRef.current.children;
    const rootStyles = getComputedStyle(rootRef.current);
    const offset = parseInt(rootStyles?.borderInlineStartWidth) + parseInt(rootStyles?.paddingInlineStart) || 0;
    const checked = Array.from(items).find(elt => elt.matches('input:checked'));
    const rootMeta = rootRef.current?.getBoundingClientRect();
    const itemMeta = checked?.nextElementSibling?.getBoundingClientRect();
    if (!rootMeta || !itemMeta) return;
    setIndicatorStyle({
      '--sci-pos': `${itemMeta.left - rootMeta.left - offset}px`,
      '--sci-width': `${itemMeta.width}px`,
      '--sci-height': `${itemMeta.height}px`,
      '--animate': shouldAnimate.current ? 'initial' : ' '
    });
  }
  useLayoutEffect(() => {
    let prevInlineSize = 0;
    observerRef.current = new ResizeObserver(entries => {
      const inlineSize = entries.at(0)?.borderBoxSize.at(0)?.inlineSize || 0;
      const diff = Math.abs(inlineSize - prevInlineSize);
      prevInlineSize = inlineSize;
      if (diff >= 0.5) {
        updateIndicator();
      }
    });
    if (rootRef.current) {
      observerRef.current.observe(rootRef.current);
    }
    return () => {
      observerRef.current?.disconnect();
    };
  }, []);
  React.useEffect(() => {
    shouldAnimate.current = true;
    updateIndicator();
  }, [value]);
  React.useEffect(() => {
    shouldAnimate.current = false;
  }, [indicatorStyle]);
  return /*#__PURE__*/_jsxs(Base, {
    tagName: "fieldset",
    innerRef: rootRef,
    className: clsx(styles.root, styles[`size-${size}`], className),
    ...extraProps,
    children: [legend ? /*#__PURE__*/_jsx(SrOnly, {
      children: /*#__PURE__*/_jsx("legend", {
        children: legend
      })
    }) : null, /*#__PURE__*/_jsx(SegmentedControlProvider, {
      name: name,
      value: value,
      size: size,
      onValueChange: setValue,
      children: children
    }), /*#__PURE__*/_jsx("div", {
      ref: indicatorRef,
      className: styles.indicator,
      style: indicatorStyle
    })]
  });
}
function SegmentedControlItem({
  id,
  name,
  value,
  checked: checkedProp,
  defaultChecked,
  children: childrenProp,
  inputProps,
  className,
  onChange,
  ...extraProps
}) {
  const genId = React.useId();
  const inputId = id || makeId('segmented-control-item', genId);
  const context = useSegmentedControlContext('SegmentedControlItem');
  const {
    label,
    additional,
    children
  } = React.Children.toArray(childrenProp).reduce((slots, child) => {
    if (/*#__PURE__*/React.isValidElement(child) && child?.props?.slot) {
      slots[child.props.slot] = child;
      return slots;
    }
    slots.children.push(child);
    return slots;
  }, {
    label: null,
    additional: null,
    addon: null,
    children: []
  });
  const checked = (() => {
    if (typeof defaultChecked === 'boolean') {
      return undefined;
    }
    if (typeof checkedProp === 'boolean') {
      return checkedProp;
    }
    if (typeof context.value === 'string') {
      return context.value === value;
    }
    return false;
  })();
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(SrOnly, {
      children: /*#__PURE__*/_jsx("input", {
        ...inputProps,
        id: inputId,
        type: "radio",
        name: context.name || name,
        value: value,
        defaultChecked: defaultChecked,
        checked: checked,
        className: styles.input,
        onChange: evt => {
          context.onValueChange?.(value);
          onChange?.(evt);
        }
      })
    }), /*#__PURE__*/_jsxs("label", {
      htmlFor: inputId,
      className: clsx(styles.label, className),
      ...extraProps,
      children: [label ? label : /*#__PURE__*/_jsx("div", {
        className: styles.content,
        children: children
      }), additional ? /*#__PURE__*/_jsx("div", {
        children: additional
      }) : null]
    })]
  });
}
export { SegmentedControl, SegmentedControlItem };